import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/i18n";
import { legalDocs } from "@/legalContent";
import Footer from "@/components/landing/Footer";

const languages = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "hi", label: "हिं" },
];

export default function LegalPage({ doc }) {
  const { lang, change } = useLang();
  const d = legalDocs[doc][lang] || legalDocs[doc].en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  return (
    <div className="min-h-screen bg-navy-900 text-cream" data-testid={`legal-page-${doc}`}>
      <header className="border-b border-brand/10 bg-navy-900/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/" data-testid="legal-back-link" className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cream/60 hover:text-brand transition-colors duration-300">
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Early Appointments
          </Link>
          <div className="flex items-center border border-brand/30" data-testid="legal-language-toggle">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => change(l.code)}
                data-testid={`legal-lang-${l.code}`}
                className={`px-3 py-2 text-[10px] font-bold tracking-widest transition-colors duration-300 ${
                  lang === l.code ? "bg-brand text-white" : "text-cream/50 hover:text-brand-300"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16 lg:py-24">
        <h1 className="font-serif font-semibold text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="legal-title">
          {d.title}
        </h1>
        <p className="mt-4 text-xs uppercase tracking-[0.2em] text-cream/40" data-testid="legal-updated">
          {d.updated}
        </p>

        <div className="mt-12 space-y-10">
          {d.sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              <h2 className="font-serif font-semibold text-xl sm:text-2xl text-brand-300">{s.h}</h2>
              {s.ps.map((p, j) => (
                <p key={j} className="mt-3 text-sm sm:text-base text-cream/65 leading-relaxed">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-3 space-y-2">
                  {s.list.map((li, k) => (
                    <li key={k} className="flex items-start gap-3 text-sm sm:text-base text-cream/65 leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 flex-none bg-brand" aria-hidden="true" />
                      {li}
                    </li>
                  ))}
                </ul>
              )}
              {s.ps2 && s.ps2.map((p, j) => (
                <p key={`b${j}`} className="mt-3 text-sm sm:text-base text-cream/65 leading-relaxed">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
